/**
 * WalkableGrid - Gestionnaire simple de matrice de walkabilité
 * Utilise une matrice 7x7 définie manuellement
 */

import * as PIXI from 'pixi.js';

export class WalkableGrid {
  constructor(width, height) {
    this.width = width;
    this.height = height;
    this.grid = [];
    this.initializeEmpty();
  }

  initializeEmpty() {
    this.grid = [];
    for (let y = 0; y < this.height; y++) {
      const row = [];
      for (let x = 0; x < this.width; x++) {
        row.push(true);
      }
      this.grid.push(row);
    }
  }

  /**
   * Initialise la grille à partir d'une matrice prédéfinie
   * @param {Array} matrix - Matrice 2D [y][x] avec true/false
   */
  initializeFromMatrix(matrix) {
    this.grid = [];
    for (let y = 0; y < this.height; y++) {
      const row = [];
      for (let x = 0; x < this.width; x++) {
        row.push(matrix[y] && matrix[y][x] !== undefined ? matrix[y][x] : true);
      }
      this.grid.push(row);
    }
    console.log(`[WalkableGrid] Initialized from matrix ${this.width}x${this.height}`);
    this.debugPrint();
  }

  isWalkable(x, y) {
    const rx = Math.round(x);
    const ry = Math.round(y);
    
    if (rx < 0 || rx >= this.width || ry < 0 || ry >= this.height) {
      return false;
    }
    
    return this.grid[ry][rx];
  }

  setWalkable(x, y, value) {
    const rx = Math.round(x);
    const ry = Math.round(y);
    if (rx >= 0 && rx < this.width && ry >= 0 && ry < this.height) {
      this.grid[ry][rx] = value;
    }
  }

  getWalkableTiles() {
    const tiles = [];
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (this.grid[y][x]) {
          tiles.push({ x, y });
        }
      }
    }
    return tiles;
  }

  getWalkableNeighbors(x, y) {
    const neighbors = [];
    const offsets = [
      { dx: 1, dy: 0 }, { dx: -1, dy: 0 },
      { dx: 0, dy: 1 }, { dx: 0, dy: -1 },
      { dx: 1, dy: 1 }, { dx: -1, dy: -1 },
      { dx: 1, dy: -1 }, { dx: -1, dy: 1 }
    ];
    
    for (const off of offsets) {
      const nx = x + off.dx;
      const ny = y + off.dy;
      if (this.isWalkable(nx, ny)) {
        neighbors.push({ x: nx, y: ny });
      }
    }
    
    return neighbors;
  }

  findPath(fromX, fromY, toX, toY) {
    fromX = Math.round(fromX);
    fromY = Math.round(fromY);
    toX = Math.round(toX);
    toY = Math.round(toY);

    if (!this.isWalkable(fromX, fromY) || !this.isWalkable(toX, toY)) {
      return null;
    }

    if (fromX === toX && fromY === toY) {
      return [{ x: fromX, y: fromY }];
    }

    const visited = new Set();
    const queue = [{ x: fromX, y: fromY, path: [{ x: fromX, y: fromY }] }];
    visited.add(`${fromX},${fromY}`);

    while (queue.length > 0) {
      const current = queue.shift();
      const neighbors = this.getWalkableNeighbors(current.x, current.y);

      for (const neighbor of neighbors) {
        const key = `${neighbor.x},${neighbor.y}`;
        
        if (neighbor.x === toX && neighbor.y === toY) {
          return [...current.path, neighbor];
        }
        
        if (!visited.has(key)) {
          visited.add(key);
          queue.push({
            x: neighbor.x,
            y: neighbor.y,
            path: [...current.path, neighbor]
          });
        }
      }
    }

    return null;
  }

  debugPrint() {
    console.log('[WalkableGrid] Matrix:');
    for (let y = 0; y < this.height; y++) {
      let row = '';
      for (let x = 0; x < this.width; x++) {
        row += this.grid[y][x] ? '. ' : '# ';
      }
      console.log(`  ${row}| y=${y}`);
    }
  }

  createDebugOverlay(tileWidth, tileHeight) {
    const container = new PIXI.Container();
    container.name = 'walkable-debug-overlay';
    
    const graphics = new PIXI.Graphics();
    
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const screenX = (x - y) * (tileWidth / 2);
        const screenY = (x + y) * (tileHeight / 2);
        
        const halfW = tileWidth / 2;
        const halfH = tileHeight / 2;
        
        const color = this.grid[y][x] ? 0x00ff00 : 0xff0000;
        const alpha = this.grid[y][x] ? 0.2 : 0.4;
        
        graphics.beginFill(color, alpha);
        graphics.lineStyle(1, 0x000000, 0.3);
        graphics.moveTo(screenX, screenY - halfH);
        graphics.lineTo(screenX + halfW, screenY);
        graphics.lineTo(screenX, screenY + halfH);
        graphics.lineTo(screenX - halfW, screenY);
        graphics.closePath();
        graphics.endFill();
      }
    }
    
    container.addChild(graphics);
    return container;
  }
}

export default WalkableGrid;
